<template>
  <div class="container">
    <div class="wrapper">
      <!-- 
        <p style="word-wrap: break-word;word-break: break-all;">ユーザーID：{{ authStore.tokenLoginInfo?.userId }}</p>
        <p style="word-wrap: break-word;word-break: break-all;">トークン　：{{ authStore.tokenLoginInfo?.accessToken }}</p>
      -->
      <p v-if="isError" class="align-center">認証に失敗しました</p>
      <loading v-else :message="'認証処理中...'"></loading>

      <!-- 差し戻されたり削除済みの契約書を元にする可能性もあるので制限は設けない -->
      <button class="btn warning" v-on:click="sendFailedMessage">
        キャンセル
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { useAuth } from '@/composable/auth/auth'
import { useOperationSupportLog } from '@/composable/operation_support/log'
import { RouterConst } from '@/const/Router'
import router from '@/routers'
import { DialogService } from '@/service/DialogService'
import { useAuthStore } from '@/store/auth'
import { OperationSupportLog } from '@/types/OperationSupport'
import { CookieUtil } from '@/utils/CookieUtil'
import { onBeforeMount } from 'vue'
import { defineComponent, onMounted, ref } from 'vue'

export default defineComponent({
  setup() {
    const isError = ref(false)
    const authStore = useAuthStore()
    const { tokenLogin } = useAuth()
    const { saveLog } = useOperationSupportLog()

    const LOG_SCR_NAME = 'トークン認証画面'

    const sendLoginScreesLoadingMessage = async (): Promise<boolean> => {
      try {
        window.TokenLoginScreenLoading.postMessage('')
        return true
      } catch {
        return false
        
      }
    }

    onBeforeMount(async () => {
      // ネイティブアプリにトークンログイン画面の読み込みが始まったことを通知する
      await sendLoginScreesLoadingMessage()
    })

    const sendLoginScreesAccessMessage = async (): Promise<boolean> => {
      try {
        window.TokenLoginScreenAccess.postMessage('')
        return true
      } catch {
        return false
      }
    }

    const sendFailedMessage = async (): Promise<boolean> => {
      isError.value = true
      try {
        window.LoginFailed.postMessage('')
        return true
      } catch {
        return false
      }
    }

    const sendSuccessMessage = async (): Promise<boolean> => {
      try {
        window.LoginSuccess.postMessage('')
        return true
      } catch {
        return false
      }
    }

    const getCookie = async (): Promise<boolean> => {
      try {
        const cookies = document.cookie
        const accessToken = decodeURIComponent(await CookieUtil.getValue(cookies, 'access_token'))
        const userId = parseInt(decodeURIComponent(await CookieUtil.getValue(cookies, 'user_id')), 10)

        try {
          const log = new OperationSupportLog()
          await log.setUserInfo(userId)
          await log.setLogInfo(LOG_SCR_NAME, '旧方式（Cookieを使用した認証情報の受け渡し）での認証処理の事前処理を開始', 'Cookie：' + cookies)
          await saveLog(log)
        } catch (e) {
          // ログの記録失敗時に処理続行するよう握りつぶす
        }

        // await DialogService.info({
        //   html: cookies
        // }, true)

        if (accessToken && userId) {
          try {
            const log = new OperationSupportLog()
            await log.setUserInfo(userId)
            await log.setLogInfo(LOG_SCR_NAME, '旧方式（Cookieを使用した認証情報の受け渡し）での認証処理の事前処理に成功。認証処理へ移行。', 'ユーザーID：' + (userId ? userId : 'null') + '／アクセストークン：' + (accessToken ? accessToken : 'null'))
            await saveLog(log)
          } catch (e) {
            // ログの記録失敗時に処理続行するよう握りつぶす
          }

          await authStore.setTokenLoginInfo(userId, accessToken)
          return true
        } else {
          try {
            const log = new OperationSupportLog()
            await log.setUserInfo(userId)
            await log.setLogInfo(LOG_SCR_NAME, '旧方式（Cookieを使用した認証情報の受け渡し）での認証処理の事前処理に失敗。', 'ユーザーID：' + (userId ? userId : 'null') + '／アクセストークン：' + (accessToken ? accessToken : 'null'))
            await saveLog(log)
          } catch (e) {
            // ログの記録失敗時に処理続行するよう握りつぶす
          }
          return false
        }
      } catch {
        return false
      }
    }

    onMounted(async () => {
      try {
        const log = new OperationSupportLog()
        await log.setLogInfo(LOG_SCR_NAME, 'トークン認証処理の開始')
        await saveLog(log)
      } catch (e) {
        // ログの記録失敗時に処理続行するよう握りつぶす
      }

      // 新方式での認証を先に行う
      if (await sendLoginScreesAccessMessage()) {
        return
      }

      // 新方式が失敗した場合は旧方式を試す
      if (await getCookie()) {
        // 旧方式（Cookieを使用した受け渡し）が成功＝未アップデート端末からのアクセスが成功している場合ここで終える
        // ※storeに保存しているので処理がauthStore.$subscribeに移行する

        // TODO: アップデートを促すメッセージは改修後のアプリ（1.0.1）リリース後に復活させる
        // await DialogService.info({
        //   html: '旧方式での認証を行います。<br>※将来的にログインできなくなるため、お早めにアプリのアップデートを実行してください'
        // }, true)
        return
      } else {
        // 「QRコードまたはID・パスワードを確認」と言っているが実際このケースの運用が決まっていないので仮の文言
        // ※24.5.29現在トークンやQRの有効期限などが見れないので確認しようがない
        await DialogService.error({
          html: 'アプリとの連携に失敗しました<br>QRコードまたはID・パスワードを管理者に確認してください。'
        }, true)

        try {
          const log = new OperationSupportLog()
          await log.setLogInfo(LOG_SCR_NAME, 'トークン認証画面へのアクセスをアプリへ通知', '結果：失敗')
          await saveLog(log)
        } catch (e) {
          // ログの記録失敗時に処理続行するよう握りつぶす
        }

        await sendFailedMessage()
        // sendLoginScreesAccessMessageが成功した時点でstoreが更新される
        // 更新されることでauthStore.$subscribeが実行されるのonMountedの処理としてはここで終わり
      }
    })

    authStore.$subscribe(async (mutation, state) => {
      // 認証storeを監視して変更があった場合に実行する
      if (!state.tokenLoginInfo) {
        return
      }

      if (!authStore.tokenLoginInfo) {
        await DialogService.error({
          html: '認証情報の受け取りに失敗しました'
        }, true)

        try {
          const log = new OperationSupportLog()
          await log.setLogInfo(LOG_SCR_NAME, 'アプリからの認証情報受け取り', '認証情報無し')
          await saveLog(log)
        } catch (e) {
          // ログの記録失敗時に処理続行するよう握りつぶす
        }

        await sendFailedMessage()
        return
      }

      if (!authStore.tokenLoginInfo.userId) {
        await DialogService.error({
          html: 'ユーザーIDの受け取りに失敗しました'
        }, true)

        try {
          const log = new OperationSupportLog()
          await log.setLogInfo(LOG_SCR_NAME, 'アプリからの認証情報受け取り', 'ユーザーIDがnull')
          await saveLog(log)
        } catch (e) {
          // ログの記録失敗時に処理続行するよう握りつぶす
        }

        await sendFailedMessage()
        return
      }

      if (!authStore.tokenLoginInfo.accessToken) {
        await DialogService.error({
          html: 'アクセストークンの受け取りに失敗しました'
        }, true)

        try {
          const log = new OperationSupportLog()
          await log.setUserInfo(authStore.tokenLoginInfo.userId)
          await log.setLogInfo(LOG_SCR_NAME, 'アプリからの認証情報受け取り', 'アクセストークンがnull')
          await saveLog(log)
        } catch (e) {
          // ログの記録失敗時に処理続行するよう握りつぶす
        }

        await sendFailedMessage()
        return
      }

      try {
        let result = await tokenLogin(authStore.tokenLoginInfo.userId, authStore.tokenLoginInfo.accessToken)
        if (!result) {
          throw Error("サーバーから否認されました。")
        }
      } catch (e) {
        try {
          const log = new OperationSupportLog()
          await log.setUserInfo(authStore.tokenLoginInfo.userId)
          await log.setLogInfo(LOG_SCR_NAME, 'トークン認証APIの実行', '実行失敗', e)
          await saveLog(log)
        } catch (e) {
          // ログの記録失敗時に処理続行するよう握りつぶす
        }

        await sendFailedMessage()
        return
      }

      try {
        const log = new OperationSupportLog()
        await log.setLogInfo(LOG_SCR_NAME, 'トークン認証APIの実行', '正常終了')
        await saveLog(log)
      } catch (e) {
        // ログの記録失敗時に処理続行するよう握りつぶす
      }

      // 24.6.3現在、モバイルアプリ側で成功後の処理はログ記録程度しか無いので成否問わず続行する
      // ただし、今後モバイルアプリ側で運用に必要な処理を行う場合は成否判断をすること
      await sendSuccessMessage()

      // 認証画面に遷移する
      router.push({ name: RouterConst.AUTH })
    })

    return {
      document,
      authStore,
      sendFailedMessage,
      RouterConst,
      isError
    }
  }
})
</script>
<style lang="scss"></style>

<style lang="scss" scoped>
@import '@scss/pages/app-login.scss'
</style>